import { Directive, ElementRef, inject } from '@angular/core';

/**
 * Redirects wheel/trackpad scrolling to the main page scroller (`.left_block`)
 * when the pointer is over a UI region that the main scroller is NOT an ancestor
 * of — e.g. the chat box, the header/nav bar, or the empty gutters on the sides.
 *
 * The app's "page" scroll lives in a nested `.left_block` element rather than the
 * document, so the browser's native scroll-chaining cannot reach it from sibling
 * regions. Without this, scrolling inside those regions leaves the page stuck.
 *
 * Behaviour mirrors sites like Hubstaff: scrolling inside a smaller box scrolls
 * that box first, and the main page takes over only once the box hits its limit.
 *
 * Apply to an element that wraps both the main scroller and the side regions.
 */
@Directive({
  selector: '[appScrollChaining]',
  host: {
    '(wheel)': 'onWheel($event)',
  },
})
export class ScrollChainingDirective {
  private readonly _host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;

  /** Approximate pixels per line, used when a wheel reports deltas in line units. */
  private static readonly LINE_HEIGHT = 32;

  private _scroller: HTMLElement | null = null;

  protected onWheel(event: WheelEvent): void {
    // Only handle vertical intent; leave horizontal scrolling (e.g. carousels) alone.
    if (event.deltaY === 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      return;
    }

    const scroller = this.getScroller();
    // No main scroller, or it isn't the active scroll container (e.g. mobile layout
    // where the document scrolls): let the browser handle it natively.
    if (!scroller || scroller.scrollHeight <= scroller.clientHeight) {
      return;
    }

    const target = event.target instanceof Element ? event.target : null;

    // Pointer is inside the main scroller: native scroll + chaining already works.
    if (target && scroller.contains(target)) {
      return;
    }

    // Pointer is outside the main scroller (chat box, header, side gutters).
    // If a local scrollable region under the pointer can still move in this
    // direction (e.g. the chat message list), let it consume the scroll first.
    let node: Element | null = target;
    while (node && node !== this._host) {
      if (this.canScrollInDirection(node, event.deltaY)) {
        return;
      }
      node = node.parentElement;
    }

    // Nothing local can absorb it — drive the main page instead.
    event.preventDefault();
    scroller.scrollTop += this.normalizeDelta(event, scroller);
  }

  private getScroller(): HTMLElement | null {
    if (!this._scroller || !this._host.contains(this._scroller)) {
      this._scroller = this._host.querySelector<HTMLElement>('.left_block');
    }
    return this._scroller;
  }

  private canScrollInDirection(el: Element, deltaY: number): boolean {
    if (!(el instanceof HTMLElement)) {
      return false;
    }
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
      return false;
    }
    if (el.scrollHeight <= el.clientHeight) {
      return false;
    }
    return deltaY > 0
      ? el.scrollTop + el.clientHeight < el.scrollHeight - 1
      : el.scrollTop > 0;
  }

  private normalizeDelta(event: WheelEvent, scroller: HTMLElement): number {
    switch (event.deltaMode) {
      case WheelEvent.DOM_DELTA_LINE:
        return event.deltaY * ScrollChainingDirective.LINE_HEIGHT;
      case WheelEvent.DOM_DELTA_PAGE:
        return event.deltaY * scroller.clientHeight;
      default:
        return event.deltaY;
    }
  }
}
